import { camelizeKeys, camelize } from 'humps'

import { requestVerifyAddress } from '~/utils/api/address'
import { reportGroupedError } from '~/utils/logger'

import type { Address } from '~/types'

export async function performAddressVerification({
  profileId,
  address,
  dispatch,
  updateAddress,
  onSuccess,
  onError,
}: {
  profileId: string
  address: Address
  dispatch
  updateAddress: (address: Address) => Promise<boolean> | boolean
  onSuccess: (address: Address) => void
  onError: () => void
}) {
  dispatch({
    type: 'verify_request_start',
    originalAddress: address,
  })

  try {
    const response = await requestVerifyAddress(profileId, address)

    if (response.ok) {
      const body = await response.json()
      const data = camelizeKeys(body.data)

      if (data.verificationStatus === 'verified') {
        const newAddress = {
          ...address,
          isValid: true,
          lastValidatedAt: data.lastValidatedAt,
        }
        const success = await updateAddress(newAddress)

        if (success) {
          onSuccess(newAddress)
          dispatch({
            type: 'verify_flow_finish',
          })
        }
      } else {
        dispatch({
          type: 'verify_failure',
          verificationStatus: data.verificationStatus,
          changedFields: data.changedFields.map((field) => camelize(field)),
          suggestedAddress: data.suggestedAddress,
          isValid: data.isValid,
          lastValidatedAt: data.lastValidatedAt,
        })
      }
    } else {
      onError()
    }
  } catch (error) {
    dispatch({
      type: 'verify_failure',
      verificationStatus: 'not_verified',
      changedFields: [],
      suggestedAddress: address,
      isValid: false,
    })
    reportGroupedError('performAddressVerification', error)
  }

  dispatch({
    type: 'verify_request_finish',
  })
}

export async function setAddress({
  address,
  dispatch,
  updateAddress,
  onSuccess,
  onError,
}: {
  address: Address
  dispatch
  updateAddress: (address: Address) => Promise<boolean> | boolean
  onSuccess: (address: Address) => void
  onError: () => void
}) {
  try {
    const success = await updateAddress(address)

    if (success) {
      onSuccess(address)
      dispatch({
        type: 'verify_flow_finish',
      })
    }
  } catch (error) {
    reportGroupedError('acceptOriginalAddress', error)
    onError()
  }
}

export async function reenterAddress({ dispatch }: { dispatch }) {
  // By not calling `onSuccess`, we only close the modal and reset the state
  // by finishing the verify flow here.
  dispatch({
    type: 'verify_flow_finish',
  })
}
